/**
 * Authentication store using Zustand
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@/types';
import { authApi, getToken, removeToken } from '@/services/api';
import { useLanguageStore } from './languageStore';
import { useListeningResumeStore } from './listeningResumeStore';
import { useAudioPlayerStore } from './audioPlayerStore';
import { UNAUTHORIZED_EVENT } from '@/services/api/events';
import { authErrorKey, type AuthErrorKey } from '@/utils/authErrors';
import { clearOAuthPending, markOAuthPending } from '@/utils/oauthPending';
import { safeRedirect } from '@/utils/redirect';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Key of the last auth error; pages translate it (`getAuthErrorTranslation`). */
  error: AuthErrorKey | null;

  // Actions
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name: string) => Promise<boolean>;
  /** Starts Google sign-in; `redirect` (validated) is followed after the return. */
  loginWithGoogle: (redirect?: string | null) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user) => {
        set({
          user,
          isAuthenticated: user !== null,
        });
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login({ email, password });
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          set({ error: authErrorKey(error, 'login'), isLoading: false });
          return false;
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          // The confirmation email is written in the app's current language.
          const { language } = useLanguageStore.getState();
          const response = await authApi.register({ email, password, name }, language);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          set({ error: authErrorKey(error, 'register'), isLoading: false });
          return false;
        }
      },

      loginWithGoogle: async (redirect = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.getGoogleAuthUrl();
          // OAuthTokenHandler accepts the returning token only in a tab that started a sign-in.
          markOAuthPending(safeRedirect(redirect));
          window.location.href = response.url;
        } catch (error) {
          clearOAuthPending();
          set({ error: authErrorKey(error, 'google'), isLoading: false });
        }
      },

      logout: () => {
        authApi.logout();
        // Clear persisted auth storage
        localStorage.removeItem('auth-storage');
        // "Nastavi slušanje" lives on the device: the next person to use it must not see it.
        // Close the player first, or its next timeupdate would record the position again.
        useAudioPlayerStore.getState().closePlayer();
        useListeningResumeStore.getState().clear();
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      },

      checkAuth: async () => {
        const token = getToken();
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true });
        try {
          // Verify token by fetching current user from backend
          const user = await authApi.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Token is invalid or expired - clear it
          removeToken();
          set({ isAuthenticated: false, user: null, isLoading: false });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// A 401 from any API call means the stored session is no longer valid: switch to signed out.
window.addEventListener(UNAUTHORIZED_EVENT, () => {
  if (useAuthStore.getState().isAuthenticated) {
    useAuthStore.setState({ user: null, isAuthenticated: false });
  }
});

// Selector hooks for convenience
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
